import { useCallback, useEffect, useRef, useState } from 'react';
import { integrationsAPI } from '../services/integrationsAPI';
import { useToast } from '../contexts/ToastContext';
import { useResponsive } from './useResponsive';

export type SocialPlatform = 'gmail' | 'twitter' | 'youtube';

interface UseSocialConnectArgs {
  athleteId: string | null;
  // Fired after a connect attempt completes (success or failure). The caller
  // should refresh whatever connection-status state it owns. We don't return
  // a "connected" boolean because the platform-specific success criteria
  // (X requires a token exchange round-trip; Gmail/YouTube don't) live here
  // and the caller's status fetch is the source of truth anyway.
  onConnected?: () => void;
}

/**
 * Encapsulates the social-OAuth popup flow that was previously inlined in
 * SocialConfigModal. Used by:
 *   - SocialConfigModal (per-row Connect buttons in the global modal)
 *   - Analytics empty-state cards (when no socials are connected yet)
 *
 * Behavior:
 *   1. `connect(platform)` opens a popup synchronously (popup blockers fire
 *      if window.open is buried behind an await), then fetches the OAuth
 *      URL and navigates the popup to it. The synchronous open is the only
 *      reason this can't be a plain async function called from JSX.
 *   2. For X we have to stash the PKCE codeVerifier + state in
 *      sessionStorage so the callback page can exchange the code. Gmail
 *      and YouTube hand back tokens at the callback directly.
 *   3. A window 'message' listener catches the postMessage from the OAuth
 *      callback page (origin-checked), exchanges X codes if needed, and
 *      fires `onConnected` so the caller can refresh its status view.
 *   4. A watchdog releases the "Connecting…" state if that postMessage never
 *      arrives — see WATCHDOG below.
 */

// KGH-790: the flow used to have exactly one way out — the postMessage from our
// own callback page. If the user closed the popup, or the provider's login page
// hung (X's sign-in spinning on submit is what surfaced this), `connecting`
// stayed set and the row showed "Connecting…" forever with no way to retry.
// The watchdog gives it two more exits: the popup closing, and a hard time cap.
const POPUP_POLL_MS = 500;
// Generous — a first-time connect can involve a password manager, 2FA and a
// consent screen. This is a stuck-state release valve, not an OAuth deadline.
const CONNECT_TIMEOUT_MS = 3 * 60 * 1000;
// The callback page closes itself right after posting its message, so "closed"
// is also the happy path. Wait briefly before calling a close abandonment, so
// the message handler (which may be awaiting the X token exchange) can claim it.
const CLOSE_GRACE_MS = 1500;

export function useSocialConnect({ athleteId, onConnected }: UseSocialConnectArgs) {
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [connecting, setConnecting] = useState<SocialPlatform | null>(null);
  // Latest onConnected ref so the message listener (which has a stable
  // closure) always calls the freshest callback without re-subscribing on
  // every render.
  const onConnectedRef = useRef(onConnected);
  useEffect(() => {
    onConnectedRef.current = onConnected;
  }, [onConnected]);

  // Watchdog timers for the in-flight attempt, and a flag the message handler
  // sets synchronously so the watchdog can tell "finished" from "abandoned".
  const watchdogRef = useRef<{ poll?: number; timeout?: number; grace?: number }>({});
  const oauthCompletedRef = useRef(false);

  const clearWatchdog = useCallback(() => {
    const { poll, timeout, grace } = watchdogRef.current;
    if (poll) window.clearInterval(poll);
    if (timeout) window.clearTimeout(timeout);
    if (grace) window.clearTimeout(grace);
    watchdogRef.current = {};
  }, []);

  // Never leave timers running after the component goes away.
  useEffect(() => clearWatchdog, [clearWatchdog]);

  // OAuth callback listener — installed once and kept for the component's
  // lifetime. The callback page sends `{ type: 'oauth_callback', success,
  // platform, code, errorDescription, ... }`.
  useEffect(() => {
    const handleOAuthMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (data?.type !== 'oauth_callback') return;

      // Claim the attempt synchronously, before any await: the popup closes
      // itself right about now, and the watchdog must not call this abandoned.
      oauthCompletedRef.current = true;
      clearWatchdog();

      if (!data.success || data.error) {
        const errorMsg = data.errorDescription || data.error || 'Authorization failed';
        toast.error(`Failed to connect ${data.platform}: ${errorMsg}`);
        setConnecting(null);
        return;
      }

      try {
        if (data.platform === 'x' && data.code && athleteId) {
          const storedDataStr = sessionStorage.getItem('oauth_data');
          if (storedDataStr) {
            // Claim the PKCE data synchronously, before any await. This hook
            // is mounted by several components at once, so every instance's
            // listener receives this same postMessage — but the X auth code
            // is single-use. Only the listener that wins the removeItem may
            // exchange it; the rest find no oauth_data and stand down instead
            // of racing X with an already-consumed code (which surfaced as a
            // bogus "Request failed with code 400" toast next to the success
            // toast).
            sessionStorage.removeItem('oauth_data');
            const storedData = JSON.parse(storedDataStr);
            if (storedData.codeVerifier) {
              await integrationsAPI.exchangeToken(athleteId, 'x', storedData.codeVerifier, data.code);
              toast.success('Successfully connected to X!');
            }
          }
        } else if ((data.platform === 'youtube' || data.platform === 'gmail') && data.userId) {
          const platformName = data.platform === 'youtube' ? 'YouTube' : 'Gmail';
          toast.success(`Successfully connected to ${platformName}!`);
        }
      } catch (error: any) {
        console.error('OAuth callback error:', error);
        toast.error(`Failed to complete authorization: ${error.message || 'Unknown error'}`);
      } finally {
        setConnecting(null);
        onConnectedRef.current?.();
      }
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [athleteId, toast, clearWatchdog]);

  /**
   * Release the stuck "Connecting…" state when the OAuth round-trip never
   * reports back. Always re-checks real status afterwards rather than assuming
   * failure — the attempt may well have succeeded and only lost its message.
   */
  const startWatchdog = useCallback(
    (popup: Window, platform: SocialPlatform, platformLabel: string) => {
      clearWatchdog();
      oauthCompletedRef.current = false;

      const abandon = (message: string) => {
        clearWatchdog();
        setConnecting(null);
        toast.info(message);
        // Status is the source of truth — if it did connect, the row corrects
        // itself and the toast is merely redundant.
        onConnectedRef.current?.();
      };

      watchdogRef.current.poll = window.setInterval(() => {
        let closed = false;
        try {
          closed = popup.closed;
        } catch {
          // Cross-origin access to `.closed` is allowed, but never gamble the
          // interval on it throwing in some browser.
          return;
        }
        if (!closed || oauthCompletedRef.current || watchdogRef.current.grace) return;

        watchdogRef.current.grace = window.setTimeout(() => {
          if (oauthCompletedRef.current) return;
          abandon(`${platformLabel} wasn't connected — the sign-in window closed before it finished.`);
        }, CLOSE_GRACE_MS);
      }, POPUP_POLL_MS);

      watchdogRef.current.timeout = window.setTimeout(() => {
        if (oauthCompletedRef.current) return;
        // Leave the popup open — they may still be mid-login, and closing it
        // would throw away that work. Finishing still fires the message
        // handler, which connects them for real.
        abandon(`Still waiting on ${platformLabel}. Finish signing in in the other window, then use Refresh Status.`);
      }, CONNECT_TIMEOUT_MS);
    },
    [clearWatchdog, toast]
  );

  const connect = useCallback(async (platform: SocialPlatform) => {
    // Connecting socials is open to free users now — the paywall is the
    // 3-free-programs cap at the unlock/send step, not the connect step
    // (2026-08-26). No isPaid gate here.
    if (!athleteId) {
      // Callers (SocialConnectPanel) gate the Connect button on athleteId
      // already, so this branch is mostly defensive. Keep the copy honest:
      // the fix is to finish profile setup, not "try again".
      toast.error('Add your athlete profile first to connect channels.');
      return;
    }

    const apiPlatform = platform === 'twitter' ? 'x' : platform;
    const platformLabel = platform === 'twitter' ? 'X' : platform === 'gmail' ? 'Gmail' : 'YouTube';

    // Open the popup IMMEDIATELY on the click tick — any await before this
    // and mobile Safari (and most desktop browsers) block it.
    const popup = isMobile
      ? window.open('about:blank', '_blank')
      : window.open('about:blank', `${apiPlatform}_oauth`, 'width=600,height=700,scrollbars=yes,resizable=yes');

    if (!popup) {
      toast.warning('Please allow popups for this site to connect your social accounts.');
      return;
    }

    try {
      setConnecting(platform);
      const authData = await integrationsAPI.getAuthUrl(apiPlatform as 'youtube' | 'x' | 'gmail', athleteId);

      if (apiPlatform === 'x' && authData.codeVerifier) {
        sessionStorage.setItem('oauth_data', JSON.stringify({
          codeVerifier: authData.codeVerifier,
          state: authData.state,
          platform: apiPlatform,
        }));
      }

      popup.location.href = authData.authUrl;
      startWatchdog(popup, platform, platformLabel);
    } catch (error: any) {
      console.error(`Error connecting to ${platform}:`, error);
      // Staging doesn't run the social integrations container, so any
      // fetch failure on this path is the expected "service not deployed"
      // shape rather than a real outage. Friendlier copy beats a confusing
      // raw network error. Prod stays loud so we notice actual outages.
      const isStaging = process.env.REACT_APP_ENV === 'staging';
      const isNetworkError = /failed to fetch|networkerror|load failed/i.test(error?.message || '');
      const friendlyMsg = (isStaging && isNetworkError)
        ? 'Social integrations are prod-only — test the connect flow there.'
        : `Failed to connect to ${platform}: ${error.message || 'Unknown error'}`;
      toast.error(friendlyMsg);
      clearWatchdog();
      popup.close();
      setConnecting(null);
    }
  }, [athleteId, isMobile, toast, startWatchdog, clearWatchdog]);

  return { connect, connecting };
}
